import React, { useState, useEffect } from 'react'
import { ColumnDefinition } from 'common/components/DataTable'
import GenericList from 'common/components/GenericList'
import { Button, Form } from 'react-bootstrap'
import * as Icon from 'react-bootstrap-icons'
import { useNavigate } from 'react-router-dom'
import Select from 'react-select'
import { ActionMeta, SingleValue } from 'react-select'
import API from 'common/logic/API'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import EditAssetTransactionWorkflow from './EditAssetTransactionWorkflow'
import RealizedGainModal from './RealizedGainModal'
import { AssetTransaction, FinancialAsset, FinancialAssetsTotal, Fund } from 'types'
import Utils from 'common/Utils'

interface AssetOption {
    value: number
    label: string
}

function AssetTransactionsList() {
    const navigate = useNavigate()
    const [modal, setModal] = useState<{ type: string; id?: number } | null>(null)
    const [dataKey, setDataKey] = useState(Date.now())
    const [assets, setAssets] = useState<FinancialAsset[]>([])
    const [investmentFunds, setInvestmentFunds] = useState<Fund[]>([])

    const [assetFilter, setAssetFilter] = useState('')
    const [fundFilter, setFundFilter] = useState('')
    const [typeFilter, setTypeFilter] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [realizedGain, setRealizedGain] = useState<string | null>(null)
    const [showRealizedGainModal, setShowRealizedGainModal] = useState(false)

    useEffect(() => {
        API.multiple({
            assets: API.get('/financial-assets'),
            funds: API.get('/funds')
        }).then((r: any) => {
            setAssets(r.assets?.results ?? [])
            const funds = (r.funds?.results ?? []) as Fund[]
            setInvestmentFunds(funds.filter(f => f.isInvestment && !f.deleted))
        }).catch(() => {})
    }, [])

    const hideDialogs = () => setModal(null)
    const refreshData = () => setDataKey(Date.now())

    useEffect(() => {
        API.get('/asset-transactions/realized-gain-summary')
            .then((r: any) => {
                const total = r.data as FinancialAssetsTotal
                setRealizedGain(total?.value ?? '0')
            })
            .catch(() => setRealizedGain(null))
    }, [dataKey])

    const endpointParams: Record<string, unknown> = {}
    if (assetFilter) endpointParams.asset = assetFilter
    if (fundFilter) endpointParams.fund = fundFilter
    if (typeFilter) endpointParams.transaction_type = typeFilter
    if (startDate) endpointParams.start_date = startDate
    if (endDate) endpointParams.end_date = endDate

    const columns = [
        new ColumnDefinition<AssetTransaction>('Date', 'date', 'td-size-s', null, 'date'),
        new ColumnDefinition<AssetTransaction>('Asset', obj =>
            <a href={`/investments/assets/${obj.financialAsset.id}`}>{obj.financialAsset.name}</a>
        , null, null, 'asset'),
        new ColumnDefinition<AssetTransaction>('Type', obj => obj.transactionType === 'BUY' ? 'Buy' : 'Sell', 'td-size-s'),
        new ColumnDefinition<AssetTransaction>('Asset amount', obj =>
            <>{Utils.formatPreciseAmount(obj.assetAmount, true)}</>, 'td-currency', 'td-right', 'asset_amount'
        ),
        new ColumnDefinition<AssetTransaction>('Euro amount', obj =>
            <>{Utils.formatMoney(obj.euroAmount, true)} €</>, 'td-currency', 'td-right', 'euro_amount'
        ),
        new ColumnDefinition<AssetTransaction>('Price per item', obj => {
            const assetAmount = Number(obj.assetAmount)
            const euroAmount = Number(obj.euroAmount)
            const pricePerItem = obj.pricePerItem == null ? null : Number(obj.pricePerItem)
            const computed = Number.isFinite(assetAmount) && Number.isFinite(euroAmount) && assetAmount !== 0
                ? Math.abs(euroAmount) / Math.abs(assetAmount)
                : NaN
            const value = Number.isFinite(pricePerItem as number) ? pricePerItem as number : computed
            return Number.isFinite(value)
                ? <>{Utils.formatPreciseAmount(value)} €</>
                : <span className="text-muted">—</span>
        }, 'td-currency', 'td-right'),
        new ColumnDefinition<AssetTransaction>('Realized gain', obj => {
            if (obj.transactionType === 'BUY') return <span className="text-muted">—</span>
            const value = Number(obj.realizedGain)
            const cls = value > 0 ? 'text-success' : value < 0 ? 'text-danger' : ''
            return <span className={cls}>{Utils.formatMoney(obj.realizedGain, true)} €</span>
        }, 'td-currency', 'td-right', 'realized_gain'),
        new ColumnDefinition<AssetTransaction>('Notes', obj =>
            <small className="text-muted">{obj.notes}</small>
        , null, null, 'notes'),
        new ColumnDefinition<AssetTransaction>('Actions', obj => <>
            <Button variant="link" title="Edit" onClick={() => setModal({ type: 'edit', id: obj.id })}><Icon.Pencil /></Button> |
            <Button variant="link" title="Delete" onClick={() => setModal({ type: 'delete', id: obj.id })}><Icon.Trash /></Button>
        </>)
    ]

    let modalEl: React.ReactNode = null
    if (modal) {
        const completion = (result: unknown) => { hideDialogs(); if (result) refreshData() }
        switch (modal.type) {
            case 'create-buy':
                modalEl = <EditAssetTransactionWorkflow onSuccess={completion} mode="buy" />
                break
            case 'create-sell':
                modalEl = <EditAssetTransactionWorkflow onSuccess={completion} mode="sell" />
                break
            case 'edit':
                modalEl = <EditAssetTransactionWorkflow onSuccess={completion} entryId={modal.id} />
                break
            case 'delete':
                modalEl = <OperationWorkflowModal
                    parameters={modal}
                    loadingStep={(p) => API.get(`/asset-transactions/${(p as any).id}/realized-gain-impact`).then((r: any) => r.data).catch(() => null)}
                    confirmationStep={(_, loaded) => {
                        const base = "Delete this transaction? The asset amount will be reversed."
                        const impact = loaded as { hasLaterSells: boolean; count: number } | null
                        if (impact?.hasLaterSells) {
                            return <>
                                <p>{base}</p>
                                <p className="text-warning">
                                    ⚠ This will invalidate the realized gain of {impact.count} later sale
                                    transaction{impact.count === 1 ? '' : 's'} of the same asset.
                                    The API will recompute the realized gain of all affected transactions if you confirm.
                                </p>
                            </>
                        }
                        return base
                    }}
                    executionStep={(p) => API.delete(`/asset-transactions/${(p as any).id}`)}
                    completionCallback={completion}
                />
                break
        }
    }

    const hasFilters = assetFilter || fundFilter || typeFilter || startDate || endDate

    const assetOptions: AssetOption[] = assets.map(a => ({
        value: a.id,
        label: `${a.name} (${a.fund.name})`
    }))
    const selectedAssetOption = assetFilter
        ? assetOptions.find(option => option.value === Number(assetFilter)) ?? null
        : null

    const handleAssetFilterChange = (selected: SingleValue<AssetOption>, _meta: ActionMeta<AssetOption>) => {
        setAssetFilter(selected ? String(selected.value) : '')
        setDataKey(Date.now())
    }

    const filterBar = (
        <div className="mb-2 d-flex align-items-center" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
            <Form.Control
                as="select"
                size="sm"
                value={fundFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setFundFilter(e.target.value); setAssetFilter(''); setDataKey(Date.now()) }}
                style={{ width: '150px' }}
            >
                <option value="">All investment funds</option>
                {investmentFunds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </Form.Control>
            <div style={{ width: '220px' }}>
                <Select<AssetOption>
                    classNamePrefix="asset-select"
                    isClearable
                    options={assetOptions}
                    value={selectedAssetOption}
                    onChange={handleAssetFilterChange}
                    placeholder="Search for an asset..."
                    styles={{
                        control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            fontSize: '0.875rem'
                        })
                    }}
                />
            </div>
            <Form.Control
                as="select"
                size="sm"
                value={typeFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setTypeFilter(e.target.value); setDataKey(Date.now()) }}
                style={{ width: '120px' }}
            >
                <option value="">All</option>
                <option value="BUY">Buy</option>
                <option value="SELL">Sell</option>
            </Form.Control>
            <Form.Control
                size="sm"
                type="date"
                value={startDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setStartDate(e.target.value); setDataKey(Date.now()) }}
                style={{ width: '150px' }}
                title="From date"
            />
            <span style={{ color: '#888' }}>→</span>
            <Form.Control
                size="sm"
                type="date"
                value={endDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEndDate(e.target.value); setDataKey(Date.now()) }}
                style={{ width: '150px' }}
                title="To date"
            />
            {hasFilters && (
                <Button size="sm" variant="link" onClick={() => {
                    setAssetFilter(''); setFundFilter(''); setTypeFilter(''); setStartDate(''); setEndDate(''); setDataKey(Date.now())
                }}>Clear filters</Button>
            )}
        </div>
    )

    return (
        <>
            {modalEl}
            {showRealizedGainModal && <RealizedGainModal onHide={() => setShowRealizedGainModal(false)} />}
            <GenericList
                title="Investment Transactions"
                columns={columns as unknown as ColumnDefinition[]}
                endpoint="/asset-transactions"
                endpointParams={endpointParams}
                beforeTable={<>
                    <p>
                        <Button variant="link" onClick={() => setModal({ type: 'create-buy' })}>Buy</Button>
                        {' | '}
                        <Button variant="link" onClick={() => setModal({ type: 'create-sell' })}>Sell</Button>
                        {' | '}
                        <Button variant="link" onClick={() => navigate('/investments/transactions/import')}>Import</Button>
                    </p>
                    <div className="mb-2">
                        {realizedGain !== null && <span>
                            <strong>Realized gains:</strong>{' '}
                            <Button variant="link" className="p-0 align-baseline" onClick={() => setShowRealizedGainModal(true)} title="Click to filter by date range">
                                <strong style={{ color: Number(realizedGain) > 0 ? '#28a745' : Number(realizedGain) < 0 ? '#dc3545' : undefined }}>
                                    {Utils.formatMoney(realizedGain, true)} €
                                </strong>
                            </Button>
                        </span>}
                    </div>
                    {filterBar}
                </>}
                dataKey={dataKey}
            />
        </>
    )
}

export default AssetTransactionsList
